import React, { useEffect, useState } from 'react';
import { FaArrowRight, FaFileAlt, FaEnvelope, FaChevronDown } from 'react-icons/fa';
import HeroPortrait from './illustrations/HeroPortrait';
import DeliveredLetterTrail from './illustrations/DeliveredLetterTrail';
import { FadeIn } from './animations/MotionPrimitives';
import { SOCIALS } from './Navigation';
import resume from './assets/Adiba_Akter_resume.pdf';
import './styles/Hero.css';

function scrollTo(id) {
  const target = document.getElementById(id);
  if (!target) return;
  const reduce =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
}

/*
 * The hero uses the same FadeIn reveal as every chapter below it. That is a
 * deliberate accessibility choice as much as a consistency one: FadeIn's
 * default rendered state is visible, so the recruiter's very first screen can
 * never be left blank by a stalled animation (throttled tab, automation,
 * slow device) — see useRevealOnce in animations/MotionPrimitives.
 */
function Hero() {
  const [scrolled, setScrolled] = useState(false);

  /* the scroll invitation steps back once the recruiter has taken it up */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section className="hero" id="home">
      <div className="hero-inner">
        <div className="hero-copy">
          <FadeIn as="span" className="hero-status" delay={0.06}>
            <span className="hero-status-dot" aria-hidden="true" />
            Open to full-time roles · Jan 2027
          </FadeIn>

          <FadeIn as="p" className="hero-greeting" delay={0.1}>
            Hi, I&apos;m
          </FadeIn>

          <FadeIn as="h1" className="hero-name" delay={0.14}>
            Adiba Akter
          </FadeIn>

          <FadeIn as="h2" className="hero-headline" delay={0.2}>
            Software Engineer building reliable{' '}
            <span className="hero-accent">full-stack and AI-powered</span> products.
          </FadeIn>

          <FadeIn as="p" className="hero-sub" delay={0.26}>
            I ship production frontend features, developer tools, data workflows and
            machine-learning experiments that turn complex systems into dependable
            user experiences.
          </FadeIn>

          <FadeIn className="hero-ctas" delay={0.32}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => scrollTo('projects')}
            >
              View Projects <FaArrowRight aria-hidden="true" />
            </button>
            <a
              className="btn btn-secondary"
              href={resume}
              target="_blank"
              rel="noreferrer"
            >
              <FaFileAlt aria-hidden="true" /> View Résumé
            </a>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => scrollTo('contact')}
            >
              <FaEnvelope aria-hidden="true" /> Contact
            </button>
          </FadeIn>

          <FadeIn as="p" className="hero-availability" delay={0.38}>
            Open to full-time Software Engineering and AI Engineering roles starting
            January 2027.
          </FadeIn>
        </div>

        <FadeIn className="hero-visual" delay={0.12} y={18}>
          {/* the courier's delivery, told in the empty corridor between the
              copy and the portrait — it crosses no text, button or image */}
          <DeliveredLetterTrail />
          <HeroPortrait />
        </FadeIn>
      </div>

      <a
        className={`hero-cue ${scrolled ? 'hero-cue--stepped-back' : ''}`}
        href="#about"
        onClick={(event) => {
          event.preventDefault();
          scrollTo('about');
        }}
      >
        <span className="hero-cue-label">
          <svg
            className="hero-cue-letter"
            width="15"
            height="11"
            viewBox="0 0 15 11"
            fill="none"
            aria-hidden="true"
            focusable="false"
          >
            <rect
              x="0.8"
              y="0.8"
              width="13.4"
              height="9.4"
              rx="1.6"
              fill="#FFFDF8"
              stroke="currentColor"
              strokeWidth="1.3"
            />
            <path
              d="M1.6 1.6 7.5 6l5.9-4.4"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Explore my work
          <FaChevronDown className="hero-cue-arrow" aria-hidden="true" />
        </span>
        <span className="hero-cue-line" aria-hidden="true" />
      </a>

      <span className="sr-only">Contact email: {SOCIALS.email}</span>
    </section>
  );
}

export default Hero;
